import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  BankActions,
  DetailsAction,
  FavoredContactsActions,
  PixActions,
  SettingsActions,
  TransactionsActions,
} from '@portal/actions';
import { SET_NEW_CONTACT } from '@portal/actions/actionTypes';
import { decreaseLoading, increaseLoading } from '@portal/actions/loading';

import NavigationService from '~/services/navigation';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import ConfirmationDetails from '@portal/components/ConfirmationDetails/ConfirmationDetails';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import NewContactModal from '@portal/components/NewContactModal/NewContactModal';
import ValueRequestedDetails from '@portal/components/ValueRequestedDetails/ValueRequestedDetails';

import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import AdvancedContactList from '@portal/components/AdvancedContactList/AdvancedContactList';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedSteps from '@portal/components/AdvancedSteps/AdvancedSteps';
import OverdraftConfirmationModal from '@portal/components/OverdraftConfirmationModal/OverdraftConfirmationModal';
import { getRouteStackPath } from '@portal/config/routes';
import { BankAccountType } from '@portal/enum/accountType';
import { TransactionStatus } from '@portal/enum/approvalsType';
import { TransferType } from '@portal/enum/transfers';
import { useNavigation } from '@portal/hooks/useNavigation';
import { useOverdraft } from '@portal/hooks/useOverdraft';
import useReduxState from '@portal/hooks/useReduxState';
import { DetailType, TransactionType } from '@portal/models/enumerators';
import AccountApi from '@portal/repositories/account';
import { encrypt, signData } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';
import { formatCurrency } from '@portal/services/masks';
import * as MessageService from '@portal/services/message';
import { safeString } from '@portal/utils/safeString';
import { DateTime } from 'luxon';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';

enum TabIndexEnum {
  CONTACT = '1',
  VALUE = '2',
  CONFIRMATION = '3',
  DESCRIPTION = '4',
}

const Transfer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    bank: { list: banks },
    favoredContacts: { list: contacts, newContact },
    details: { transaction, type, error },
    settings: { settings },
  } = useReduxState();

  const [tabKey, setTabKey] = useState('1');
  const [isNewContactModalVisible, setIsNewContactModalVisible] =
    useState(false);
  const [favored, setFavored] = useState<models.Contact | null>(null);
  const [moneyValue, setMoneyValue] = useState('0');
  const [rawMoneyValue, setRawMoneyValue] = useState(0);
  const [description, setDescription] = useState('');
  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);
  const [hasLoadedContacts, setHasLoadedContacts] = useState(false);
  const [transactionScheduleAt, setTransactionScheduleAt] = useState(
    DateTime.now()
  );
  const [showOverdraftModalState, setShowOverdraftModalState] = useState(false);
  const [overdraftOptinRequired, setOverdraftOptinRequired] = useState(false);
  const [overdraftOptinChecked, setOverdraftOptinChecked] = useState(false);

  const {
    overdraftAmount,
    checkOverdraftUsage,
    confirmOverdraftUsage,
    cancelOverdraftUsage,
  } = useOverdraft();

  useEffect(() => {
    if (newContact) {
      handleClickContact(newContact);
      dispatch({
        type: SET_NEW_CONTACT,
        payload: null,
      });
    }
  }, [dispatch, newContact]);

  useEffect(() => {
    if (contacts.length === 0 && !hasLoadedContacts) {
      dispatch(FavoredContactsActions.getFavoredContact());
      setHasLoadedContacts(true);
    }
  }, [contacts, dispatch, hasLoadedContacts]);

  useEffect(() => {
    if (error) {
      const errorKey = error.message.toUpperCase();
      MessageService.error(`PIX.${errorKey}`);
    }
  }, [error]);

  useEffect(() => {
    if (transaction) {
      if (type === DetailType.NEW) {
        setTabKey('4');
      } else {
        if (
          transaction &&
          transaction.status === TransactionStatus.PENDING_APPROVE
        ) {
          navigate(
            `/aprovacao/detalhe-aprovacao/${transaction.transactionRequestPendingApprovalId}`
          );
          return;
        }

        NavigationService.navigate(
          `${getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS')}${
            transaction?.transactionRequestId
              ? transaction?.transactionRequestId
              : transaction?.id
          }`,
          {
            isExtract: true,
            isScheduled: !!transaction?.transactionRequestId,
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, transaction, type]);

  useEffect(() => {
    if (!settings) {
      dispatch(SettingsActions.getSettings());
    }
  }, [dispatch, settings]);

  useEffect(() => {
    dispatch(BankActions.getBanks());

    return () => {
      dispatch(DetailsAction.unsetDetails());
    };
  }, [dispatch]);

  const handleCreateNewContact = (contact: models.Contact) => {
    if (contact.skipRegistration) {
      setIsNewContactModalVisible(false);
      handleClickContact(contact);
      return;
    }

    dispatch(FavoredContactsActions.createFavoredContact(contact));
    setIsNewContactModalVisible(false);
  };

  const handleClickContact = (contact: models.Contact) => {
    setTabKey('2');
    setFavored(contact);
  };

  const handleValueRequested = () => {
    if (!favored) {
      return MessageService.error('PAGES.HOME.TRANSFER.ERROR.CONTACT', {
        domain: 'validation',
      });
    }

    const limits = settings?.limits?.pix;
    if (
      moneyValue === '0' ||
      rawMoneyValue === 0 ||
      (limits?.max && rawMoneyValue > limits.max) ||
      rawMoneyValue < (limits?.min ?? 0)
    ) {
      return MessageService.error('INVALID_AMOUNT');
    }

    if (favored.pixKey) {
      setTabKey('3');
    } else {
      setTabKey('3');
    }
  };

  const handleAdvanceToPin = async () => {
    const totalAmount = rawMoneyValue;
    const willUseOverdraft = checkOverdraftUsage(totalAmount);
    if (willUseOverdraft) {
      dispatch(increaseLoading());
      setShowOverdraftModalState(true);
      setOverdraftOptinChecked(false);
      try {
        const hasHash = await AccountApi.getOverdraftHash();
        setOverdraftOptinRequired(!hasHash);
      } catch (e) {
        setOverdraftOptinRequired(true);
      } finally {
        dispatch(decreaseLoading());
      }
      return;
    }
    setOpenConfirmationModal(true);
  };

  const handleOverdraftConfirmation = async () => {
    if (overdraftOptinRequired) {
      if (!overdraftOptinChecked) return;
      dispatch(increaseLoading());
      try {
        await AccountApi.postOverdraftSignature(
          translate('MODALS.OVERDRAFT_CONFIRMATION.OPTIN_CHECKBOX')
        );
        setOverdraftOptinRequired(false);
      } catch (e) {
        dispatch(decreaseLoading());
        return;
      }
      dispatch(decreaseLoading());
    }
    confirmOverdraftUsage();
    setShowOverdraftModalState(false);
    setOpenConfirmationModal(true);
  };

  const handleOverdraftCancel = () => {
    cancelOverdraftUsage();
    setShowOverdraftModalState(false);
    setOverdraftOptinChecked(false);
    setOverdraftOptinRequired(false);
  };

  const handleConfirmation = async (pin: string) => {
    setOpenConfirmationModal(false);
    const timestamp = new Date().getTime().toString();
    const amount = rawMoneyValue * 100;

    const transactionHash = signData(amount, favored?.pixKey ?? '', timestamp);
    const encryptedPIN = await encrypt(pin);
    if (
      transaction?.pixInfo &&
      transactionScheduleAt.diffNow('days').days > 0
    ) {
      return dispatch(
        TransactionsActions.pixScheduledTransfer(
          { ...transaction.pixInfo, description: description || '' },
          rawMoneyValue,
          {
            pin: encryptedPIN,
            transactionHash,
            timestamp,
          },
          transactionScheduleAt.toISODate()
        )
      );
    }

    if (transaction?.pixInfo) {
      return dispatch(
        TransactionsActions.pixTransfer(
          { ...transaction.pixInfo, description: description || '' },
          rawMoneyValue,
          {
            pin: encryptedPIN,
            transactionHash,
            timestamp,
          }
        )
      );
    }

    if (favored && 'psp' in favored) {
      const pixAgencyInfo = {
        pspId: favored.psp ?? '',
        taxId: favored.taxIdentifier ?? '',
        branch: favored.bankAgency ?? '',
        account: favored.bankAccount ?? '',
        bankAccountDigit: favored.bankAccountDigit ?? '',
        accountType: favored.accountType ?? BankAccountType.CURRENT,
        description: description || '',
        name: favored.alias,
      };

      const pixInfo = {
        name: safeString(favored.alias),
        bank: safeString(favored.bankUserName),
        account: safeString(favored.bankAccount),
        accountDigit: safeString(favored.bankAccountDigit),
        branch: safeString(favored.bankAgency),
        taxIdentifier: safeString(favored.taxIdentifier),
        bankId: safeString(favored.bankCode),
        accountType: Number(safeString(favored.accountType)),
        description: description || '',
      };

      const type = TransactionType.PIX;
      const isScheduled = transactionScheduleAt.diffNow('days').days > 0;

      if (isScheduled) {
        return dispatch(
          TransactionsActions.pixAgencyScheduleTransfer(
            rawMoneyValue,
            {
              pin: encryptedPIN,
              transactionHash,
              timestamp,
            },
            pixInfo,
            transactionScheduleAt.toISODate(),
            type
          )
        );
      }

      return dispatch(
        TransactionsActions.pixAgencyTransfer(
          rawMoneyValue,
          {
            pin: encryptedPIN,
            transactionHash,
            timestamp,
          },
          pixAgencyInfo,
          pixInfo
        )
      );
    }
  };

  const AdvancedButtonContainer = (
    <div className="transfers__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="text"
        onClick={() => {
          setFavored(null);
          setMoneyValue('0');
          setTabKey('1');
        }}
      />
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
        onClick={handleValueRequested}
      />
    </div>
  );

  const DescriptionButtons = (
    <div className="transfers__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="text"
        onClick={() => {
          setTabKey('2');
        }}
      />
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
        onClick={() => {
          if (favored?.pixKey) {
            dispatch(
              PixActions.getPixInfo(favored.pixKey ?? '', rawMoneyValue)
            );
          } else {
            setTabKey('4');
          }
        }}
      />
    </div>
  );

  const ConfirmPayment = (
    <div className="transfers__button">
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
        variant="text"
        onClick={() => {
          setTabKey('3');
          dispatch(DetailsAction.unsetDetails());
        }}
      />
      <AdvancedButton
        text={translate('COMPONENTS.ADVANCED_BUTTON.CONFIRM')}
        onClick={handleAdvanceToPin}
      />
    </div>
  );

  const stepOptions = [
    {
      value: TabIndexEnum.CONTACT,
      label: translate('PAGES.HOME.PIX.TRANSFER.TABS.ONE'),
      children: (
        <AdvancedContactList
          testId="advanced-contact-list-pix-transfer"
          banks={banks}
          contacts={contacts}
          onClickContact={handleClickContact}
          onClickNewContact={() => setIsNewContactModalVisible(true)}
          type={TransferType.PIX}
        />
      ),
    },
    {
      value: TabIndexEnum.VALUE,
      label: translate('PAGES.HOME.PIX.TRANSFER.TABS.TWO'),
      children: (
        <>
          <p className="value-requested__how-much-text">
            {translate('PAGES.HOME.PIX.TRANSFER.HOW_MUCH')}{' '}
            <b>{favored?.alias}</b>{' '}
            {favored?.pixKey ? `(${favored?.pixKey})` : ''}
          </p>
          <ValueRequestedDetails
            value={moneyValue}
            onChange={(value, rawValue) => {
              setMoneyValue(value);
              setRawMoneyValue(rawValue);
            }}
          />
          <div className="transfers__quick-amounts">
            {[50, 100, 250, 500, 1000].map((rawValue) => (
              <AdvancedButton
                key={rawValue}
                type="button"
                onClick={() => {
                  setRawMoneyValue((prev) => {
                    const newValue = (prev || 0) + rawValue;
                    setMoneyValue(formatCurrency(newValue));
                    return newValue;
                  });
                }}
                variant="outlined"
                size="small"
                text={formatCurrency(rawValue)}
                startIcon={<PlusCircleOutlined className="transfers__icon" />}
              />
            ))}
            <AdvancedButton
              onClick={() => {
                setMoneyValue('');
                setRawMoneyValue(0);
              }}
              variant="outlined"
              size="small"
              text={translate('PAGES.HOME.PIX.TRANSFER.CLEAR')}
              startIcon={<CloseCircleOutlined className="transfers__icon" />}
            />
          </div>
        </>
      ),
    },
    {
      value: TabIndexEnum.CONFIRMATION,
      label: translate('PAGES.HOME.PIX.TRANSFER.TABS.THREE'),
      children: (
        <>
          <p className="value-requested__how-much-text">
            {translate('PAGES.HOME.PIX.TRANSFER.DESCRIPTION.TITLE')}
          </p>
          <AdvancedInput
            label={translate('PAGES.HOME.PIX.TRANSFER.DESCRIPTION.PLACEHOLDER')}
            value={description}
            onChange={setDescription}
            multiline
            rows={3}
            maxLength={140}
            helperText
            variant="outlined"
          />
        </>
      ),
    },
    {
      value: TabIndexEnum.DESCRIPTION,
      label: translate('PAGES.HOME.PIX.TRANSFER.TABS.FOUR'),
      children: (
        <>
          {favored ? (
            <ConfirmationDetails
              methodType="contact"
              destiny={favored}
              setTransactionScheduleAt={setTransactionScheduleAt}
              transactionScheduleAt={transactionScheduleAt}
              transferType={TransferType.PIX}
              value={moneyValue}
              setTabKey={setTabKey}
            />
          ) : (
            <AdvancedAlert>
              {translate('PAGES.HOME.PIX.TRANSFER.ERROR.CONTACT')}
            </AdvancedAlert>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.PIX'),
            url: getRouteStackPath('PIX', 'PIX'),
          },
          {
            active: false,
            title: translate('PAGES.HOME.PIX.TRANSFER.TITLE'),
          },
        ]}
      />
      <div className="transfer-pix">
        <AdvancedPanelContentHeader
          testId="transfer-pix-header"
          title={translate('PAGES.HOME.PIX.TRANSFER.TITLE')}
          description={translate('PAGES.HOME.PIX.DESCRIPTIONS.TRANSFER')}
          goBackAction={() => navigate(getRouteStackPath('PIX', 'PIX'))}
          buttonTitle={translate('PAGES.HOME.PIX.BUTTONS.BACK_PIX')}
        />
        <AdvancedSteps
          testId="advanced-steps-pix-transfer"
          steps={stepOptions}
          currentStep={tabKey}
          onStepChange={(step) => setTabKey(String(step))}
        />
        {tabKey === TabIndexEnum.VALUE && AdvancedButtonContainer}
        {tabKey === TabIndexEnum.CONFIRMATION && DescriptionButtons}
        {tabKey === TabIndexEnum.DESCRIPTION && ConfirmPayment}
      </div>
      <ConfirmationModal
        visible={openConfirmationModal}
        setVisible={setOpenConfirmationModal}
        handleConfirmation={handleConfirmation}
      />
      {isNewContactModalVisible ? (
        <NewContactModal
          closeModal={() => setIsNewContactModalVisible(false)}
          onSubmit={handleCreateNewContact}
          type={TransferType.PIX}
          visible={isNewContactModalVisible}
        />
      ) : null}
      <OverdraftConfirmationModal
        visible={showOverdraftModalState}
        overdraftAmount={overdraftAmount}
        onConfirm={handleOverdraftConfirmation}
        onCancel={handleOverdraftCancel}
        optinRequired={overdraftOptinRequired}
        optinChecked={overdraftOptinChecked}
        onOptinChange={setOverdraftOptinChecked}
      />
    </>
  );
};

export default Transfer;
